import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { ensureCsrfToken, readCsrfTokenFromResponse, withCsrfHeader } from '../../utils/auth/csrf'
import { requestPortalSessionReset } from '../features/portal-session-reset/portalSessionResetSlice'
import { isPortalDomain } from '../../../lib/currentDomain'
import {
    shouldAttachPortalCsrf,
    shouldInvalidatePortalSessionOnUnauthorized,
    shouldRetryPortalRequestWithFreshCsrf,
} from '../../../lib/portalRequestPolicy'
import { isScopedAccessEndedResponse, notifyScopedAccessEnded } from '../../utils/scopedAccess'

const toFetchArgs = (args: string | FetchArgs): FetchArgs => (typeof args === 'string' ? { url: args } : { ...args })

export type BaseQueryCustomError = {
    status: 'BASE_QUERY_CUSTOM_ERROR'
    error: string
    data?: unknown
}

export type BaseQueryWithReauthError = FetchBaseQueryError | BaseQueryCustomError

const toBaseQueryCustomError = (error: unknown): BaseQueryCustomError => ({
    status: 'BASE_QUERY_CUSTOM_ERROR',
    error: error instanceof Error ? error.message : 'Unknown request error',
})

const dynamicBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    BaseQueryWithReauthError,
    { baseUrl: string }
> = async (args, api, extraOptions) => {
    const baseUrl = extraOptions.baseUrl
    const cookieAuthMode = isPortalDomain
    const createRawBaseQuery = (resolvedBaseUrl: string) =>
        fetchBaseQuery({
            baseUrl: resolvedBaseUrl,
            credentials: cookieAuthMode ? 'include' : undefined,
        })

    const requestArgs = toFetchArgs(args)
    const rawBaseQuery = createRawBaseQuery(baseUrl)

    const executeQuery = async (nextRequestArgs: FetchArgs, forceCsrfRefresh = false) => {
        let finalArgs = nextRequestArgs

        if (shouldAttachPortalCsrf({ cookieAuthMode, method: nextRequestArgs.method, url: nextRequestArgs.url })) {
            const csrfToken = await ensureCsrfToken(
                async () => {
                    const handshakeResult = await rawBaseQuery({ url: '/csrf/handshake' }, api, extraOptions)
                    if (handshakeResult.error) {
                        throw handshakeResult.error
                    }

                    return readCsrfTokenFromResponse(handshakeResult.meta?.response)
                },
                { forceRefresh: forceCsrfRefresh }
            )

            finalArgs = {
                ...nextRequestArgs,
                headers: withCsrfHeader(nextRequestArgs.headers, csrfToken),
            }
        }

        return rawBaseQuery(finalArgs, api, extraOptions)
    }

    try {
        let result = await executeQuery(requestArgs)

        if (
            result.error &&
            shouldRetryPortalRequestWithFreshCsrf({
                cookieAuthMode,
                method: requestArgs.method,
                url: requestArgs.url,
                status: result.error.status,
            })
        ) {
            result = await executeQuery(requestArgs, true)
        }

        return result
    } catch (error) {
        if (isFetchBaseQueryError(error)) {
            return { error }
        }
        return { error: toBaseQueryCustomError(error) }
    }
}

export const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    BaseQueryWithReauthError,
    { baseUrl: string }
> = async (args, api, extraOptions) => {
    const requestArgs = toFetchArgs(args)
    const result = await dynamicBaseQuery(args, api, extraOptions)

    if (result && result.error && isScopedAccessEndedResponse(result.error)) {
        notifyScopedAccessEnded()
    }

    if (
        result &&
        result.error &&
        shouldInvalidatePortalSessionOnUnauthorized({ status: result.error.status, url: requestArgs.url })
    ) {
        api.dispatch(requestPortalSessionReset())
    }
    return result
}

export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    if (typeof error !== 'object' || error == null || !('status' in error)) {
        return false
    }

    const status = error.status
    return (
        typeof status === 'number' ||
        status === 'FETCH_ERROR' ||
        status === 'PARSING_ERROR' ||
        status === 'TIMEOUT_ERROR' ||
        status === 'CUSTOM_ERROR'
    )
}
